import React, { useEffect, useState } from "react";
import { getOptimizedImageUrl } from "../lib/cms";

export default function LightboxModal({ images = [], currentIndex = 0, isOpen = false, onClose }) {
  const [index, setIndex] = useState(currentIndex);

  useEffect(() => {
    setIndex(currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    }

    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, index, images.length]);

  if (!isOpen || !images || images.length === 0) return null;

  const currentImage = images[index];

  const handlePrev = () => {
    setIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Galleri forstørret visning"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4 sm:p-6 select-none"
      onClick={onClose}
    >
      {/* Top bar with count & close */}
      <div
        className="absolute top-4 left-4 right-4 flex items-center justify-between text-white/90 z-20"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-sm font-medium tracking-wide bg-black/40 px-3 py-1.5 rounded-full border border-white/10 backdrop-blur-sm">
          {index + 1} / {images.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Luk galleri"
          className="w-10 h-10 rounded-full bg-black/50 hover:bg-black/80 border border-white/20 text-white flex items-center justify-center transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#D9691E]"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
        </button>
      </div>

      {/* Navigation Buttons */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
            aria-label="Forrige billede"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/60 hover:bg-[#1E4231] text-white border border-white/20 flex items-center justify-center transition-colors cursor-pointer shadow-lg focus:outline-none focus:ring-2 focus:ring-[#D9691E]"
          >
            <i className="fa-solid fa-chevron-left text-lg" aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            aria-label="Næste billede"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/60 hover:bg-[#1E4231] text-white border border-white/20 flex items-center justify-center transition-colors cursor-pointer shadow-lg focus:outline-none focus:ring-2 focus:ring-[#D9691E]"
          >
            <i className="fa-solid fa-chevron-right text-lg" aria-hidden="true" />
          </button>
        </>
      )}

      {/* Main Image container */}
      <div
        className="relative max-w-6xl max-h-[85vh] flex items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={getOptimizedImageUrl(currentImage, 1600, 85)}
          alt={`Caspers Træservice udført arbejde ${index + 1}`}
          className="max-h-[82vh] max-w-full w-auto object-contain rounded-lg shadow-2xl transition-transform duration-300"
        />
      </div>

      {/* Bottom hint for keyboard / touch */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/60 tracking-wider hidden sm:block">
        Brug piletasterne eller klik for at skifte billede • ESC for at lukke
      </div>
    </div>
  );
}
